import { faBox } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MDXProvider } from '@mdx-js/react';
import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { DocsLayout } from '../../-components/DocsLayout';
import { mdxComponents } from '../../-components/MDXComponents';
import { findComponent } from '../../-registry';

// Discover shared component docs (live in -shared/ folder)
const sharedModules = import.meta.glob('../../-shared/*.docs.mdx');

// Discover UI primitive docs
const primitiveModules = import.meta.glob('../../-primitives/*.docs.mdx');

const slugToModule: Record<string, () => Promise<unknown>> = {};
for (const [path, loader] of Object.entries(sharedModules)) {
  const slug = path.split('/').pop()!.replace('.docs.mdx', '');
  slugToModule[slug] = loader;
}
for (const [path, loader] of Object.entries(primitiveModules)) {
  const slug = path.split('/').pop()!.replace('.docs.mdx', '');
  slugToModule[slug] = loader;
}

export const Route = createFileRoute('/docs/components/$slug/')({
  component: ComponentDocPage,
});

function ComponentDocPage() {
  const { slug } = Route.useParams();
  const meta = findComponent(slug);

  const DocContent = useMemo(() => {
    const loader = slugToModule[slug];
    if (!loader) return null;
    return lazy(loader as () => Promise<{ default: React.ComponentType }>);
  }, [slug]);

  if (!DocContent) {
    return (
      <DocsLayout>
        <div className='flex flex-col items-center justify-center py-24 gap-3 text-center'>
          <FontAwesomeIcon
            icon={faBox}
            className='size-10 text-muted-foreground/30'
          />
          <p className='text-sm font-medium text-foreground'>
            No docs yet for <code className='text-primary'>{slug}</code>
          </p>
          <p className='text-xs text-muted-foreground max-w-xs'>
            Create{' '}
            <code className='text-muted-foreground'>
              routes/docs/-shared/{meta?.name ?? slug}.docs.mdx
            </code>{' '}
            to add documentation.
          </p>
        </div>
      </DocsLayout>
    );
  }

  return (
    <DocsLayout>
      <MDXProvider components={mdxComponents}>
        <Suspense fallback={<DocSkeleton />}>
          <DocContent />
        </Suspense>
      </MDXProvider>
    </DocsLayout>
  );
}

function DocSkeleton() {
  return (
    <div className='space-y-4'>
      <Skeleton className='h-9 w-48' />
      <Skeleton className='h-4 w-96' />
      <Skeleton className='h-4 w-80' />
      <div className='mt-8 space-y-3'>
        <Skeleton className='h-6 w-32' />
        <Skeleton className='h-32 w-full' />
      </div>
    </div>
  );
}
